import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import sethLogo from "@/assets/seth-logo.png";
import { NAV, wa } from "@/lib/site";

const Header = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        document.body.style.overflow = open ? "hidden" : "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [open]);

    return (
        <header
            data-testid="header-navigation"
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
                scrolled || open
                    ? "bg-[#070709]/85 backdrop-blur-xl border-b border-line"
                    : "bg-transparent border-b border-transparent"
            }`}
        >
            <div className="mx-auto max-w-7xl px-5 sm:px-8 h-[72px] flex items-center justify-between gap-6">
                <a href="#inicio" data-testid="header-logo" aria-label="SETH Soluções Digitais — Início">
                    <img
                        src={sethLogo}
                        alt="SETH Soluções Digitais"
                        className="h-8 sm:h-9 w-auto"
                    />
                </a>

                <nav className="hidden lg:flex items-center gap-8" aria-label="Navegação principal">
                    {NAV.map((item) => (
                        <a
                            key={item.href}
                            href={item.href}
                            className="text-[13px] font-medium tracking-wide text-ink2 hover:text-gold transition-colors duration-300"
                        >
                            {item.label}
                        </a>
                    ))}
                </nav>

                <div className="flex items-center gap-3">
                    <a
                        href={wa()}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="header-cta-button"
                        className="btn-gold relative hidden sm:inline-flex items-center rounded-full px-5 py-2.5 text-[13px] font-semibold tracking-wide"
                    >
                        Falar com a SETH
                    </a>
                    <button
                        type="button"
                        data-testid="mobile-menu-toggle"
                        aria-label={open ? "Fechar menu" : "Abrir menu"}
                        aria-expanded={open}
                        onClick={() => setOpen((v) => !v)}
                        className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-full border border-line text-ink hover:border-gold/60 hover:text-gold transition-colors duration-300"
                    >
                        {open ? <X size={18} /> : <Menu size={18} />}
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                        className="lg:hidden overflow-hidden border-t border-line bg-[#070709]/95 backdrop-blur-xl"
                    >
                        <nav className="px-6 py-8 flex flex-col gap-1" aria-label="Menu mobile">
                            {NAV.map((item, i) => (
                                <motion.a
                                    key={item.href}
                                    href={item.href}
                                    data-testid={`mobile-nav-${i + 1}`}
                                    onClick={() => setOpen(false)}
                                    initial={{ opacity: 0, x: -16 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.06 * i, duration: 0.4 }}
                                    className="py-3.5 border-b border-line/60 font-display text-xl font-semibold text-ink hover:text-gold transition-colors duration-300"
                                >
                                    {item.label}
                                </motion.a>
                            ))}
                            <a
                                href={wa()}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="mobile-cta-button"
                                className="btn-gold relative mt-6 inline-flex items-center justify-center rounded-full px-6 py-3.5 text-sm font-semibold tracking-wide"
                            >
                                Falar com a SETH
                            </a>
                        </nav>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
};

export default Header;
